import { useToast } from 'src/utils/toast'
import React from 'react'
import ConfigPanel, { PanelState } from 'src/companies/configurations/config-panel'
import {
  flattenGroupedTenantConfigurations,
  groupTenantConfigurations,
  mergeConfigurationWithValue,
} from 'src/utils/tenant-configuration'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'
import ConfigurationPanelForm from './ConfigurationPanelForm'
import ConfigurationPanelDetail from './ConfigurationPanelDetails'
import { useNavigate } from 'react-router-dom'

type ConfigurationPanelProps = Readonly<{
  configurations: TenantConfigResponse[]
  data: Tenant[]
  onSave: (data: KeyValuePair[]) => Promise<any>
  onEditCancel?: () => void
  editPermission: string
  title?: string
  alwaysExpanded?: boolean
  openInEdit?: boolean
  navigateToOnDoneEditing?: string
  secondaryControls?: React.ReactNode
  encryptedKeys?: string[]
}>

const FormSchema = Yup.object({
  key: Yup.string().nullable(),
  value: Yup.string().nullable(),
})

export default function ConfigurationPanel({
  configurations,
  data,
  onSave,
  onEditCancel,
  editPermission,
  title = '',
  alwaysExpanded = false,
  openInEdit = false,
  navigateToOnDoneEditing = undefined,
  secondaryControls = undefined,
  encryptedKeys = [],
}: ConfigurationPanelProps) {
  const toast = useToast()
  const navigate = useNavigate()
  const [aggregatorState, setAggregatorState] = React.useState<PanelState>(
    openInEdit ? 'editing' : 'collapsed'
  )
  const [formData, setFormData] = React.useState({ submittedAt: null, isSubmitting: false })

  const [mergedConfig, setMergedConfig] = React.useState<TenantConfiguration[]>([])

  const handleConfigSave = async (aggregatorConfig: KeyValuePair[]) => {
    await onSave(aggregatorConfig)
    stopEditing()
  }

  React.useEffect(() => {
    if (configurations) {
      setMergedConfig(mergeConfigurationWithValue(configurations, data))
    }
  }, [configurations, data])

  const stopEditing = () => {
    setAggregatorState('expanded')
    setFormData({ submittedAt: null, isSubmitting: false })

    if (navigateToOnDoneEditing) {
      navigate(navigateToOnDoneEditing)
    }
  }

  const groupedConfigs = groupTenantConfigurations(mergedConfig)

  return (
    <ConfigPanel
      label={title}
      expanded={alwaysExpanded || ['expanded', 'editing'].includes(aggregatorState)}
      hideToggleIcon={alwaysExpanded}
      panelState={aggregatorState}
      formData={formData}
      editPermission={editPermission}
      onChange={(e, expanded) => {
        if (aggregatorState === 'editing') return
        setAggregatorState(expanded ? 'expanded' : 'collapsed')
      }}
      onClickEdit={() => setAggregatorState('editing')}
      onCancelEdit={() => {
        stopEditing()
        onEditCancel?.()
      }}
      onSave={() => setFormData((state) => ({ ...state, submittedAt: new Date().getTime() }))}
      secondaryControls={secondaryControls}
    >
      {aggregatorState !== 'editing' && (
        <ConfigurationPanelDetail tenantConfig={groupedConfigs} encryptedKeys={encryptedKeys} />
      )}
      {aggregatorState === 'editing' && (
        <Formik
          initialValues={{ fields: groupedConfigs }}
          enableReinitialize={true}
          validateOnChange={false}
          validationSchema={FormSchema}
          onSubmit={async (values, _formikActions) => {
            setFormData((state) => ({ ...state, isSubmitting: true }))

            try {
              await handleConfigSave(flattenGroupedTenantConfigurations(values.fields))
              _formikActions.setSubmitting(false)
            } catch (error) {
              toast({
                status: 'error',
                title: 'Error',
                description: error?.response?.data?.message ?? 'Failed to update aggregator config',
              })
              setFormData({ submittedAt: null, isSubmitting: false })
              _formikActions.setErrors(error?.response?.data?.validation_messages)
              _formikActions.setSubmitting(false)
            }
          }}
        >
          {(formikProps: FormikProps<any>) => (
            <ConfigurationPanelForm
              formikProps={formikProps}
              formData={formData}
              formFields={formikProps.values.fields}
            />
          )}
        </Formik>
      )}
    </ConfigPanel>
  )
}
